// ────────────────────────────────────────────────────
// src/features/cashRegister/hooks/useCashRegisters.ts
// ────────────────────────────────────────────────────
import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryKey,
} from '@tanstack/react-query';
import {
  fetchRegisters,
  createRegister,
  toggleRegisterActive,
  deleteRegister,
  CashRegister,
  ToggleRegisterResponse,
} from '../api/cashFlowApi';

const LIST_KEY: QueryKey = ['cashRegisters', 'all'];

export function useCashRegisters() {
  const qc = useQueryClient();

  /* ── helpers ────────────────────────────────────── */
  const refresh = () => qc.invalidateQueries({ queryKey: LIST_KEY });

  /* ── catalogue (active + inactive) ──────────────── */
  const registersQ = useQuery<CashRegister[], Error>({
    queryKey : LIST_KEY,
    queryFn  : () => fetchRegisters(false),
    staleTime: 60_000,
  });

  /* ── create register ────────────────────────────── */
  const addMut = useMutation({
    mutationFn: (name: string) =>
      createRegister({ register_name: name }),
    onSuccess : refresh,
  });

  /* ── toggle active flag ─────────────────────────── */
  type ToggleVars = { id: number; active: boolean };
  const toggleMut = useMutation<ToggleRegisterResponse, Error, ToggleVars>({
    mutationFn: ({ id, active }) =>
      toggleRegisterActive({ id_register: id, active }),
    onError: err => {
      // Swallow harmless “already in that state” errors
      if (String(err?.message).includes('No state change')) refresh();
    },
    onSuccess: refresh,
  });

  /* ── delete (hard → soft‑fallback) ──────────────── */
  const delMut = useMutation<void, Error, number>({
    async mutationFn(id) {
      /* 1️⃣ try the real DELETE first */
      try {
        await deleteRegister(id);
        return;                                     // ✅ done
      } catch (err) {
        /* 2️⃣ hard‑delete still 500s → fallback to toggle */
        try {
          await toggleRegisterActive({ id_register: id, active: false });
          return;                                   // ✅ soft‑deleted
        } catch (toggleErr: any) {
          /* ‑ ignore harmless “already inactive” 400s */
          if (String(toggleErr?.message).includes('No state change')) return;
          /* ‑ otherwise re‑throw so the UI can surface it */
          throw toggleErr;
        }
      }
    },
    /* refresh regardless of *how* we got here */
    onSettled: refresh,
  });

  return { ...registersQ, addMut, toggleMut, delMut };
}
